import { ORPCError } from "@orpc/client";
import { Button, IconButton } from "@rosc/ui/v32/button";
import { ConfirmSheet } from "@rosc/ui/v32/confirm-sheet";
import { EmptyState } from "@rosc/ui/v32/empty-state";
import { Field } from "@rosc/ui/v32/field";
import { Input, Textarea } from "@rosc/ui/v32/input";
import { Notice } from "@rosc/ui/v32/notice";
import { SkeletonRow } from "@rosc/ui/v32/skeleton";
import { Loader2, Mic, Pencil, RotateCw, Trash2 } from "lucide-react";
import type { JSX } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Macro } from "@/common/types/macros";
import { WithDictation } from "@/components/ui/DictationButton";
import { toast } from "@/hooks/use-toast";
import {
  getVisitScope,
  useVisitScope,
  type VisitScope,
} from "@/lib/visit-queries";
import { deleteMacro, getMacros, saveMacro } from "@/services/macros";

const EMPTY = { macroPublicId: "", name: "", section: "", details: "" };

// v3.2 (@rosc/ui/v32): the form and the list are two panels, edge to edge
// on phones and rounded cards from 640px, like the other Templates tabs.
const CARD =
  "overflow-hidden border-y border-v3-border bg-v3-panel font-v3 text-v3-text max-sm:-mx-4 sm:rounded-v3-panel sm:border-0 sm:shadow-v3-hairline-strong";
const GROUP =
  "mb-2 px-1 font-v3 text-[12.5px] font-semibold uppercase tracking-wide text-v3-text-3";

// Macros (stored as macros, once called spoken shortcuts): saying the
// trigger phrase while dictating a note edit inserts the saved wording into
// the named section.
export const SpokenShortcuts = (): JSX.Element => {
  const scope = useVisitScope();
  return (
    <SpokenShortcutsForm
      key={`${scope?.uid}:${scope?.accountPublicId}:${scope?.generation}`}
      scope={scope}
    />
  );
};
const SpokenShortcutsForm = ({
  scope,
}: {
  scope: VisitScope | null;
}): JSX.Element => {
  const mounted = useRef(true);
  const formRef = useRef<HTMLElement>(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const current = useCallback(
    () => mounted.current && !!scope && getVisitScope() === scope,
    [scope],
  );
  const { t } = useTranslation();
  const [macros, setMacros] = useState<Macro[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Macro | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    if (!current()) return;
    setLoadFailed(false);
    try {
      const values = await getMacros(scope?.uid);
      if (current()) setMacros(values);
    } catch {
      if (!current()) return;
      setLoadFailed(true);
      setMacros([]);
    }
  }, [scope, current]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async (): Promise<void> => {
    if (!current()) return;
    setSaving(true);
    try {
      await saveMacro(draft, scope?.uid);
      if (!current()) return;
      toast({ variant: "success", title: t("shortcuts.savedToast") });
      setDraft(EMPTY);
      await load();
    } catch {
      if (!current()) return;
      toast({ variant: "error", title: t("shortcuts.saveError") });
    } finally {
      if (current()) setSaving(false);
    }
  };

  const handleDelete = async (macro: Macro): Promise<void> => {
    if (!current()) return;
    setDeleting(true);
    try {
      await deleteMacro(macro.public_id, scope?.uid);
    } catch (error) {
      if (!current()) return;
      // Already gone (e.g. deleted in another tab): refresh instead of failing.
      if (!(error instanceof ORPCError && error.code === "NOT_FOUND")) {
        toast({ variant: "error", title: t("shortcuts.deleteError") });
        setDeleting(false);
        return;
      }
    }
    if (!current()) return;
    toast({ variant: "success", title: t("shortcuts.deletedToast") });
    setPendingDelete(null);
    setDeleting(false);
    setDraft((value) =>
      value.macroPublicId === macro.public_id ? EMPTY : value,
    );
    await load();
  };

  const canSave =
    draft.name.trim() && draft.details.trim() && !saving && !!scope;

  const editing = draft.macroPublicId !== "";

  return (
    <div className="min-w-0 space-y-6 font-v3" data-testid="spoken-shortcuts">
      <section
        ref={formRef}
        aria-labelledby="shortcut-form-title"
        className={`${CARD} scroll-mt-24 px-4 py-5 sm:p-6`}
      >
        <div className="space-y-1">
          <h2
            className="text-[17px] font-semibold leading-[22px] text-v3-text"
            id="shortcut-form-title"
          >
            {t(editing ? "shortcuts.editTitle" : "shortcuts.newTitle")}
          </h2>
          <p className="max-w-[68ch] text-[13.5px] leading-5 text-v3-text-2">
            {t("shortcuts.description")}
          </p>
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <Field htmlFor="shortcut-trigger" label={t("shortcuts.triggerLabel")}>
            <WithDictation
              fieldLabel={t("dictation.field.fields.shortcutTrigger")}
            >
              <Input
                id="shortcut-trigger"
                maxLength={64}
                placeholder={t("shortcuts.triggerPlaceholder")}
                value={draft.name}
                onChange={(event) => {
                  setDraft({ ...draft, name: event.target.value });
                }}
              />
            </WithDictation>
          </Field>
          <Field htmlFor="shortcut-section" label={t("shortcuts.sectionLabel")}>
            <WithDictation
              fieldLabel={t("dictation.field.fields.shortcutSection")}
            >
              <Input
                id="shortcut-section"
                maxLength={32}
                placeholder={t("shortcuts.sectionPlaceholder")}
                value={draft.section}
                onChange={(event) => {
                  setDraft({ ...draft, section: event.target.value });
                }}
              />
            </WithDictation>
          </Field>
          <Field
            className="sm:col-span-2"
            htmlFor="shortcut-wording"
            label={t("shortcuts.wordingLabel")}
          >
            <WithDictation
              fieldLabel={t("dictation.field.fields.shortcutWording")}
              multiline
            >
              <Textarea
                className="min-h-28"
                id="shortcut-wording"
                maxLength={8000}
                placeholder={t("shortcuts.wordingPlaceholder")}
                value={draft.details}
                onChange={(event) => {
                  setDraft({ ...draft, details: event.target.value });
                }}
              />
            </WithDictation>
          </Field>
        </div>
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {editing && (
            <Button
              variant="secondary"
              onClick={() => {
                setDraft(EMPTY);
              }}
            >
              {t("common.cancel")}
            </Button>
          )}
          <Button
            disabled={!canSave}
            variant="primary"
            onClick={() => void handleSave()}
          >
            {saving && <Loader2 aria-hidden className="animate-spin" />}
            {t("shortcuts.saveButton")}
          </Button>
        </div>
      </section>

      <section aria-labelledby="shortcut-list-title" className="space-y-3">
        <h2 className={GROUP} id="shortcut-list-title">
          {t("shortcuts.listTitle")}
        </h2>

        {loadFailed && (
          <Notice
            actions={
              <Button
                className="w-full sm:w-auto"
                variant="secondary"
                onClick={() => void load()}
              >
                <RotateCw aria-hidden />
                {t("templates.assistant.retry")}
              </Button>
            }
            testId="shortcuts-load-error"
            title={t("shortcuts.loadError")}
            tone="error"
          />
        )}

        {macros === null && (
          <div
            aria-hidden
            className={`${CARD} divide-y divide-v3-border`}
            data-testid="shortcuts-loading"
          >
            <SkeletonRow />
            <SkeletonRow />
          </div>
        )}

        {macros?.length === 0 && !loadFailed && (
          <div className={CARD}>
            <EmptyState
              className="px-5 py-10"
              icon={<Mic />}
              testId="shortcuts-empty"
              title={t("shortcuts.empty")}
            />
          </div>
        )}

        {macros && macros.length > 0 && (
          <ul className={`${CARD} divide-y divide-v3-border`}>
            {macros.map((macro) => (
              <li
                key={macro.public_id}
                className="flex min-h-[72px] min-w-0 items-start gap-3 py-3 pl-4 pr-2 sm:pl-5 sm:pr-3"
              >
                <span
                  aria-hidden
                  className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-v3-surface-2 text-v3-text-2 shadow-v3-hairline [&_svg]:size-[18px]"
                >
                  <Mic />
                </span>
                <div className="min-w-0 flex-1 pt-0.5">
                  <p className="wrap-break-word text-[15px] font-semibold leading-5 text-v3-text">
                    {macro.name || "—"}
                  </p>
                  <p className="mt-0.5 text-[12.5px] leading-[17px] text-v3-text-3">
                    {t("shortcuts.insertsInto", {
                      section: macro.section || "—",
                    })}
                  </p>
                  <p className="mt-1.5 whitespace-pre-line wrap-break-word text-[13.5px] leading-5 text-v3-text-2">
                    {macro.details}
                  </p>
                </div>
                <div className="flex shrink-0 items-center">
                  <IconButton
                    label={t("shortcuts.editTitle")}
                    onClick={() => {
                      setDraft({
                        macroPublicId: macro.public_id,
                        name: macro.name,
                        section: macro.section,
                        details: macro.details,
                      });
                      formRef.current?.scrollIntoView?.({
                        behavior: "smooth",
                        block: "start",
                      });
                    }}
                  >
                    <Pencil />
                  </IconButton>
                  <IconButton
                    className="hover:bg-v3-rec-tint hover:text-v3-rec-text"
                    label={t("shortcuts.deleteLabel")}
                    onClick={() => {
                      setPendingDelete(macro);
                    }}
                  >
                    <Trash2 />
                  </IconButton>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <ConfirmSheet
        busy={deleting}
        cancelLabel={t("common.cancel")}
        confirmLabel={t("shortcuts.deleteButton")}
        description={
          <span className="wrap-break-word">
            {t("shortcuts.deleteDescription", {
              name: pendingDelete?.name ?? "",
            })}
          </span>
        }
        destructive
        open={pendingDelete !== null}
        stayOpen
        testId="shortcut-delete"
        title={t("shortcuts.deleteTitle")}
        onConfirm={() => {
          if (pendingDelete) void handleDelete(pendingDelete);
        }}
        onOpenChange={(open) => {
          if (!open && !deleting) setPendingDelete(null);
        }}
      />
    </div>
  );
};
